import { CloseIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Container,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import Creators from "../../../resources/images/authors.png";
import Characters from "../../../resources/images/characters.png";
import Community from "../../../resources/images/community-orders.png";
import CinematicUniverses from "../../../resources/images/mcu.jpg";
import StoryArcs from "../../../resources/images/events.png";
import Groups from "../../../resources/images/groups.png";
import MainOrders from "../../../resources/images/main-orders.png";
import MyOrders from "../../../resources/images/my-orders.png";
import Requests from "../../../resources/images/redwing-premium.png";
import { searchReadingOrders } from "../../../services/searchApi";
import BrowseCard from "../components/BrowseCard";
import BrowseSectionTitle from "../components/BrowseSectionTitle";
import DiscoverRow from "../components/DiscoverRow";
import { trackEvent } from "../../../shared/analytics";
import SearchResults from "../components/SearchResults";
import { DISCOVER_SECTIONS } from "../helpers/discoverSections";

// Single column like mobile on phones, two columns once there is room.
const BrowseCardGrid = ({ children }) => (
  <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="12px" spacingY="0">
    {children}
  </SimpleGrid>
);

const Browse = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { isLoading, data, mutate, reset } = useMutation(() =>
    searchReadingOrders(searchTerm)
  );

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm && searchTerm.length >= 3) {
        trackEvent("browse_search_performed", { searchTerm });
        mutate();
      }
    }, 750);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, mutate]);

  return (
    <Container maxW="container.lg">
      <Flex alignItems="center" justifyContent="space-between">
        <Heading as="h2" my="6">
          Browse
        </Heading>
        <Box>
          <InputGroup>
            <InputLeftElement
              pointerEvents="none"
              children={<SearchIcon color="gray.300" />}
            />
            <Input
              type="search"
              aria-label="Search reading orders"
              placeholder="Search reading orders..."
              autoCapitalize="none"
              autoCorrect="off"
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value.length < 3) {
                  reset();
                }
              }}
              value={searchTerm}
            />
            {!!searchTerm && (
              <InputRightElement
                as="button"
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setSearchTerm("");
                  reset();
                }}
                children={<CloseIcon boxSize={3} color="#8F9BB3" />}
              />
            )}
          </InputGroup>
          {!!searchTerm && searchTerm.length < 3 && (
            <Text fontSize="11px" color="#8F9BB3" mt="6px" ml="4px">
              Type at least 3 characters to search
            </Text>
          )}
        </Box>
      </Flex>
      {searchTerm && searchTerm.length >= 3 ? (
        <Box>
          <BrowseSectionTitle>SEARCH RESULTS</BrowseSectionTitle>
          <SearchResults
            results={data?.data}
            isLoading={isLoading}
            searchTerm={searchTerm}
          />
        </Box>
      ) : (
        <>
          <Box>
            {DISCOVER_SECTIONS.map((section) => (
              <DiscoverRow key={section.key} section={section} />
            ))}
          </Box>

          <BrowseSectionTitle>COMMUNITY READING ORDERS</BrowseSectionTitle>
          <BrowseCardGrid>
            <BrowseCard
              to="/browse/community/list"
              title="Community"
              description="Browse reading orders created and shared by the Redwing community."
              thumbnail={Community}
            />
          </BrowseCardGrid>

          <BrowseSectionTitle>REDWING READING ORDERS</BrowseSectionTitle>
          <BrowseCardGrid>
            <BrowseCard
              to="/browse/main/list"
              title="Main Reading orders"
              description="Explore complete reading orders for major comic sagas."
              thumbnail={MainOrders}
            />
            <BrowseCard
              to="/browse/story-arcs/list"
              title="Story Arcs & Events"
              description="Discover key story arcs and events from across the universe."
              thumbnail={StoryArcs}
            />
            <BrowseCard
              to="/browse/characters/list"
              title="Characters"
              description="Follow a character's journey from their first appearance onward."
              thumbnail={Characters}
            />
            <BrowseCard
              to="/browse/groups/list"
              title="Groups"
              description="Explore teams and groups throughout comic book history."
              thumbnail={Groups}
            />
            <BrowseCard
              to="/browse/creators/list"
              title="Creators' Runs"
              description="Explore standout runs from your favorite creators."
              thumbnail={Creators}
            />
            <BrowseCard
              to="/browse/cinematic-universes/list"
              title="Cinematic Universes"
              description="Read the comics that inspired your favorite series and movies."
              thumbnail={CinematicUniverses}
            />
          </BrowseCardGrid>

          <BrowseSectionTitle>MY READING ORDERS</BrowseSectionTitle>
          <BrowseCardGrid>
            <BrowseCard
              to="/my-orders"
              title="My Orders"
              description="Create your own reading orders and find them all here. (Premium only)"
              thumbnail={MyOrders}
            />
            <BrowseCard
              to="/browse/requests"
              title="My Requests"
              description="Can't find what you need? Request it and we'll add it within 28 days."
              thumbnail={Requests}
            />
          </BrowseCardGrid>
        </>
      )}
    </Container>
  );
};

export default Browse;
